import { cn } from "@/lib/utils";

export type StatusPillTone = "ok" | "not-ok";

export interface StatusPillProps {
  /** Only two tones exist in the Almikia indicator grammar: green (ok) and red (not ok). */
  tone: StatusPillTone;
  /** Visible text; this is also the pill's only accessible content. */
  text: string;
  className?: string;
}

const TONE_STYLES: Record<
  StatusPillTone,
  { background: string; color: string }
> = {
  ok: { background: "#E7F5EC", color: "#23794A" },
  "not-ok": { background: "#FBE7E4", color: "#B3321C" },
};

/**
 * Check icon for the "ok" tone. Kept inline (no icon-library dependency)
 * since the pill is the only consumer and the shape is fixed by the guide.
 */
function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
    >
      <path
        d="M3 8.5L6.2 11.5L13 4.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Cross icon for the "not-ok" tone. */
function CrossIcon() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 16 16"
      width="14"
      height="14"
      fill="none"
    >
      <path
        d="M4 4L12 12M12 4L4 12"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * A two-tone status pill (green/ok or red/not-ok), always paired with an
 * icon so the state is never conveyed by color alone. No `role="status"`:
 * that role turns every pill into an ARIA live region, which is wrong for a
 * static label and noisy once several pills sit in a list or table. The
 * visible icon + text is the pill's only accessible content — the icon is
 * `aria-hidden` and the text is not duplicated into an `aria-label`, so the
 * accessible name is exactly the visible text.
 */
export function StatusPill({ tone, text, className }: StatusPillProps) {
  const { background, color } = TONE_STYLES[tone];

  return (
    <span
      data-testid="status-pill"
      data-tone={tone}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12.5px] font-medium",
        className,
      )}
      style={{ backgroundColor: background, color }}
    >
      {tone === "ok" ? <CheckIcon /> : <CrossIcon />}
      <span>{text}</span>
    </span>
  );
}
